import {
  Avatar,
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  Text,
} from "@fluentui/react-components";
import { ArrowMaximize24Regular, ArrowMinimize24Regular, Dismiss24Regular } from "@fluentui/react-icons";
import { Flex } from "components/styled";
import { useIsMobile } from "hooks/useIsMobile";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { chatHistoryAtom, chatIdAtom, chatPanelOpenAtom } from "store/UIHrPage";
import styled from "styled-components";
import { ChatIcon } from "./ChatIcon";
import { chatMessageFont } from "./chatTypography";

import { useAppTheme } from "hooks/useAppTheme";
import { MUTATION_KEYS } from "library/shared";
import { useIsMutating } from "react-query";
import { useUserData } from "state/queries/useUserData";
import chatLogo from "../../assets/chatIcon.svg";
import NewMessage from "./NewMessage";
import { sizes } from "library/constants";
import { genUUID } from "library/utils";
import parse from "html-react-parser";

export function ChatPanel() {
  const theme = useAppTheme();
  const [ratingOpen, setRatingOpen] = useState(false);
  const [chatPanelOpen, setChatPanelOpen] = useAtom(chatPanelOpenAtom);
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { data: user } = useUserData();
  const [isExpanded, setIsExpanded] = useState(false);

  const historyContainerRef = useRef<HTMLDivElement | null>(null);

  const [newMessageState, setNewMessageState] = useState({
    message: "",
    sent: false,
  });

  const chatHistory = useAtomValue(chatHistoryAtom);

  const chatSessionId = useAtomValue(chatIdAtom);

  const toggleChatPanelState = useCallback(() => {
    setChatPanelOpen((prev) => !prev);
  }, []);

  const handleChatPanelOpen = useCallback((state) => {
    setChatPanelOpen(state);
  }, []);

  const handleCloseRatingDialog = useCallback(() => {
    setRatingOpen(false);
  }, []);

  const waitingForChatbotResponse = useIsMutating({
    mutationKey: MUTATION_KEYS.sendMessageToChatbot,
  });

  const handleUpdateNewMessageState = useCallback((state) => {
    return setNewMessageState(state);
  }, []);

  return (
    <>
      <OverlayDrawer
        position="end"
        // size="small"
        open={chatPanelOpen}
        onOpenChange={(state) => {
          setChatPanelOpen(false);
        }}
        style={{
          top: `0px`,
          width: isMobile ? "100%" : isExpanded ? "900px" : "450px",
          transition: "width 0.3s ease",
        }}
      >
        <StyledHeader>
          <DrawerHeaderTitle
            style={{
              paddingRight: "20px",
            }}
            action={
              <Flex wrap="nowrap" $align="center">
                {!isMobile && (
                  <Button
                    appearance="subtle"
                    aria-label={isExpanded ? "Collapse" : "Expand"}
                    icon={isExpanded ? <ArrowMinimize24Regular /> : <ArrowMaximize24Regular />}
                    onClick={() => setIsExpanded((prev) => !prev)}
                  />
                )}
                <Button
                  appearance="subtle"
                  aria-label="Close"
                  icon={<Dismiss24Regular />}
                  onClick={() => {
                    handleChatPanelOpen(false);
                    // setRatingOpen(true);
                  }}
                />
              </Flex>
            }
          ></DrawerHeaderTitle>
        </StyledHeader>

        <StyledBody>
          {!chatSessionId && (
            <BodyLoadingLayout>
              <LoadingWrap noMarginLeft />
            </BodyLoadingLayout>
          )}
          {/* {waitingForChatbotResponse ? <AppLogoSpinner /> : null} */}

          <Flex direction="column" width={"100%"} gap={"20px"}>
            <Flex wrap="nowrap" $align="flex-end" width={"100%"}>
              <ChatIcon />
              <BotMessage expanded={isExpanded} style={{ textAlign: "left" }}>
                {t("hbh.botHello.label")}
              </BotMessage>
            </Flex>
            {(chatHistory || [])?.map((history, index) => {
              //      const parsedPrevAnswer = history?.PrevAnswer
              // ? history.PrevAnswer.split("\n").map((line, i) => (
              //     <span key={i}>
              //       {line}
              //       <br />
              //     </span>
              //   ))
              // : "";

              const parsedPrevAnswer = parse(history?.PrevAnswer || "");
              
              return (
                <>
                  {history?.PrevQuestion ? (
                    <Flex
                      wrap="nowrap"
                      justify="flex-end"
                      $align="flex-end"
                      width={"100%"}
                      key={genUUID() + index}
                    >
                      <UserMessage expanded={isExpanded}>{history?.PrevQuestion}</UserMessage>
                      <Avatar
                        image={{
                          src: "",
                        }}
                        style={{
                          width: 42,
                          height: 42,
                          border: `1px solid ${theme.palette.themePrimary}`,
                        }}
                      />
                    </Flex>
                  ) : null}
                  {history?.PrevAnswer ? (
                    <Flex
                      wrap="nowrap"
                      $align="flex-end"
                      width={"100%"}
                      key={genUUID() + index}
                    >
                      <ChatIcon />
                      <BotMessage expanded={isExpanded} style={{ textAlign: "left" }}>
                        {parsedPrevAnswer}
                      </BotMessage>
                    </Flex>
                  ) : null}
                </>
              );
            })}
            {newMessageState.sent ? (
              <Flex
                wrap="nowrap"
                $align="flex-end"
                justify="flex-end"
                width={"100%"}
              >
                <UserMessage expanded={isExpanded}>{newMessageState.message}</UserMessage>
                <Avatar
                  image={{
                    src: "",
                  }}
                  style={{
                    width: 42,
                    height: 42,
                    border: `1px solid ${theme.palette.themePrimary}`,
                  }}
                />
              </Flex>
            ) : null}
            {waitingForChatbotResponse ? (
              <Flex wrap="nowrap" $align="baseline" width={"100%"}>
                <ChatIcon />
                <LoadingWrap />
              </Flex>
            ) : null}
            <div ref={historyContainerRef} />
          </Flex>
        </StyledBody>
        <StyledFooter>
          <NewMessage
            historyContainerRef={historyContainerRef}
            newMessageState={newMessageState}
            setNewMessageState={handleUpdateNewMessageState}
          />
        </StyledFooter>
      </OverlayDrawer>
    </>
  );
}

const MessageBox = styled.div<{ expanded?: boolean }>`
  box-sizing: border-box;
  height: fit-content;
  min-height: 60px;
  width: fit-content;
  max-width: ${({ expanded }) => (expanded ? "650px" : "325px")};
  display: flex;
  ${chatMessageFont};
  align-items: center;
  border-radius: 12px;
  padding: 10px 15px;
  word-break: break-word;
  svg {
    width: 30px;
    height: 30px;
  }
`;

const BotMessage = styled.div<{ expanded?: boolean }>`
  box-sizing: border-box;
  height: fit-content;
  min-height: 60px;
  word-break: break-word;
  max-width: ${({ expanded }) => (expanded ? "650px" : "325px")};
  ${chatMessageFont};
  border-radius: 12px;
  padding: 10px 15px;

  svg {
    width: 30px;
    height: 30px;
  }

  border-bottom-left-radius: 0;
  background: ${({ theme }) => theme.palette.neutralLighter};
`;

const UserMessage = styled(MessageBox)`
  text-align: right;
  justify-content: flex-end;

  border-bottom-right-radius: 0;
  background: ${({ theme }) => theme.palette.purpleDark};
  color: ${({ theme }) => theme.palette.white};
`;

const BodyLoadingLayout = styled.div`
  display: flex;
  position: absolute;
  z-index: 1;
  opacity: 0.7;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  justify-content: center;
  align-items: center;
`;

const StyledHeader = styled(DrawerHeader)<{ bgImage?: string }>`
  padding: 10px;

  background: ${({ theme }) => theme.palette.white};
  color: ${({ theme }) => theme.palette.black};

  // border-bottom: 1px solid ${({ theme }) => theme.palette.neutralLighter};
`;

const StyledBody = styled(DrawerBody)`
  position: relative;
  padding: 10px;
  // background-color: ${({ theme }) => theme.palette.neutralLighter};
  background-color: ${({ theme }) => theme.palette.white};
  background-size: calc(15px) calc(15px);
`;

const StyledFooter = styled(DrawerFooter)`
  display: flex;
  flex-direction: column;
  padding: 10px 10px 20px;
`;

const LoadingWrap = styled.div<{ noMarginLeft?: boolean }>`
  width: 15px;
  aspect-ratio: 1;
  border-radius: 50%;
  animation: l5 1s infinite linear alternate;
  margin-top: 10px;
  margin-left: ${({ noMarginLeft }) => (noMarginLeft ? "0px" : "50px")};
  @keyframes l5 {
    0% {
      box-shadow: 20px 0 ${({ theme }) => theme.palette.neutralLight},
        -20px 0 #0002;
      background: ${({ theme }) => theme.palette.neutralLight};
    }
    33% {
      box-shadow: 20px 0 ${({ theme }) => theme.palette.neutralLight},
        -20px 0 #0002;
      background: #0002;
    }
    66% {
      box-shadow: 20px 0 #0002,
        -20px 0 ${({ theme }) => theme.palette.neutralLight};
      background: #0002;
    }
    100% {
      box-shadow: 20px 0 #0002,
        -20px 0 ${({ theme }) => theme.palette.neutralLight};
      background: ${({ theme }) => theme.palette.neutralLight};
    }
  }
`;
